// Product Family Resolver
// Pure function: maps a line_item to its canonical family_key by walking
// priority-ordered rules; falls back to a delimiter-safe system key when
// nothing matches.
//
// All consumers (Bundle Performance, Product Economics Group by Family,
// Pairs/Focus, useProductFamilies) read the family_key from the denormalized
// shopify_order_line_items.family_key column — they MUST NOT call resolveFamily
// on the fly. resolveFamily is only invoked from:
//   - order ingest hook (after line_items insert)
//   - backfill script
//   - tests / dry-run preview endpoints

import safeRegex from "safe-regex2"
import type {
  FamilyMatchCombinator,
  FamilyMatchType,
  FamilyResolution,
  LineItemForResolver,
  ProductFamilyRule,
  PropertyValueMatch,
} from "./types"

// ---------------------------------------------------------------------------
// Grammar constants — kept in sync with DB CHECK constraint
// (see supabase/migrations/20260430000001_add_product_family_rules.sql).
// ---------------------------------------------------------------------------

/** Allowed characters for any family_key (user-defined OR system fallback). */
export const FAMILY_KEY_GRAMMAR = /^[a-z0-9_-]+$/
/** Reserved prefixes — only the system resolver may emit these. */
export const FAMILY_KEY_RESERVED_PREFIX = /^(product|line_item)_/
export const FAMILY_KEY_MIN_LENGTH = 1
export const FAMILY_KEY_MAX_LENGTH = 64

/** Anti-ReDoS: cap regex pattern length and SKU length before running .test(). */
const MAX_REGEX_PATTERN_LENGTH = 256
const MAX_SKU_LENGTH = 256

// ---------------------------------------------------------------------------
// Anti-ReDoS check — backed by safe-regex2
// ---------------------------------------------------------------------------

/**
 * Reject regex patterns whose star-height ≥ 2 — the canonical ReDoS shape
 * (nested unbounded quantifiers like `(a+)+`, `((a+))*`, `(a+){2,5}`). Backed
 * by safe-regex2 (Fastify-maintained fork of safe-regex), which parses the
 * pattern via the `ret` regex AST.
 *
 * Strictly stronger than a hand-rolled paren walker: catches deeply nested
 * cases AND `{n,m}`-bounded outer quantifiers (which can still backtrack
 * factorially even with bounded m). Also handles escaped parens and char
 * classes correctly.
 *
 * Documented limitation: pure star-height analysis does NOT flag overlapping
 * alternation like `(a|a)+` or `(a|ab)+`. UI must steer tenants toward simpler
 * match types (sku_prefix/contains/suffix) for security-sensitive paths.
 *
 * Returns `true` for patterns we should NOT execute. Pairs with the
 * MAX_REGEX_PATTERN_LENGTH / MAX_SKU_LENGTH caps for defense in depth (V8 has
 * no native regex timeout).
 */
export function isLikelyUnsafeRegex(pattern: string): boolean {
  return !safeRegex(pattern)
}

// ---------------------------------------------------------------------------
// User-input validation (DAL fail-fast — mirrors DB CHECK).
// ---------------------------------------------------------------------------

export type FamilyKeyValidationError =
  | "empty"
  | "too_long"
  | "invalid_chars"
  | "reserved_prefix"

/**
 * Validate a user-supplied family_key against the DB grammar + reserved-prefix rule.
 * Returns null on success, or a stable error code suitable for i18n in callers.
 */
export function validateUserFamilyKey(key: string): FamilyKeyValidationError | null {
  if (key.length < FAMILY_KEY_MIN_LENGTH) return "empty"
  if (key.length > FAMILY_KEY_MAX_LENGTH) return "too_long"
  if (!FAMILY_KEY_GRAMMAR.test(key)) return "invalid_chars"
  if (FAMILY_KEY_RESERVED_PREFIX.test(key)) return "reserved_prefix"
  return null
}

/**
 * Auto-slugify a human-readable family name into a candidate family_key.
 * Lowercases, replaces non-grammar runs with `-`, trims leading/trailing `-`.
 *
 * Note: the result may still violate the reserved-prefix rule (e.g. input
 * "Product 123" → "product-123" passes; "Product_123" → "product_123" fails).
 * Callers must run validateUserFamilyKey() on the result.
 */
export function slugifyFamilyName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-_]+|[-_]+$/g, "")
}

// ---------------------------------------------------------------------------
// Condition matchers — compiled once per rule into a closure.
//
// Each match_type is compiled into a `(lineItem) => boolean` test at prepare
// time so the hot path (ingest + recompute over many orders) never recompiles a
// RegExp or re-scans a list per line item:
//   - sku_regex / property_value(is_regex) → RegExp compiled ONCE
//   - product_id_list / sku_list           → membership Set built ONCE
//   - sku_prefix/suffix/contains           → needle lowercased ONCE
// A condition whose match_value is malformed (or an unsafe/invalid regex)
// compiles to a never-match test — same posture as the old per-call matchers.
// ---------------------------------------------------------------------------

type ConditionTest = (lineItem: LineItemForResolver) => boolean

const NEVER: ConditionTest = () => false

/**
 * Compile a pattern into a RegExp, or null when it must not run. Mirrors the
 * runtime guards the resolver applied per-call before: length cap, safe-regex2
 * ReDoS check (isLikelyUnsafeRegex), and RegExp-compile success. Runs ONCE at
 * prepare time.
 *
 * V8 has no native regex timeout; the length caps bound worst-case work. The
 * input-length cap is enforced at match time (cheap) by the closures below.
 */
function compileSafeRegex(pattern: unknown): RegExp | null {
  if (typeof pattern !== "string" || !pattern) return null
  if (pattern.length > MAX_REGEX_PATTERN_LENGTH) return null
  if (isLikelyUnsafeRegex(pattern)) return null
  try {
    return new RegExp(pattern)
  } catch {
    return null
  }
}

function buildPropertyValueTest(value: unknown): ConditionTest {
  if (!value || typeof value !== "object" || Array.isArray(value)) return NEVER
  const cfg = value as Partial<PropertyValueMatch>
  if (typeof cfg.property_name !== "string" || !cfg.property_name) return NEVER
  if (typeof cfg.value_pattern !== "string") return NEVER
  const propertyName = cfg.property_name

  if (cfg.is_regex) {
    const re = compileSafeRegex(cfg.value_pattern)
    if (re == null) return NEVER
    return (li) => {
      const props = li.properties
      if (!props || !Array.isArray(props) || props.length === 0) return false
      for (const prop of props) {
        if (!prop || typeof prop.name !== "string" || prop.name !== propertyName) continue
        const propValue = typeof prop.value === "string" ? prop.value : ""
        if (propValue.length > MAX_SKU_LENGTH) return false
        if (re.test(propValue)) return true
      }
      return false
    }
  }

  const patternLower = cfg.value_pattern.toLowerCase()
  return (li) => {
    const props = li.properties
    if (!props || !Array.isArray(props) || props.length === 0) return false
    for (const prop of props) {
      if (!prop || typeof prop.name !== "string" || prop.name !== propertyName) continue
      const propValue = typeof prop.value === "string" ? prop.value : ""
      if (propValue.toLowerCase() === patternLower) return true
    }
    return false
  }
}

/**
 * Compile one condition's match_type + match_value into a reusable test.
 * sku_list stays EXACT/case-sensitive (Shopify persists variant SKUs with exact
 * case; tenants key inventory/COGS on it); the sku_prefix/suffix/contains
 * family stays case-INSENSITIVE.
 */
function buildConditionTest(matchType: FamilyMatchType, value: unknown): ConditionTest {
  switch (matchType) {
    case "sku_prefix": {
      if (typeof value !== "string" || !value) return NEVER
      const needle = value.toLowerCase()
      return (li) => li.sku != null && li.sku.toLowerCase().startsWith(needle)
    }
    case "sku_suffix": {
      if (typeof value !== "string" || !value) return NEVER
      const needle = value.toLowerCase()
      return (li) => li.sku != null && li.sku.toLowerCase().endsWith(needle)
    }
    case "sku_contains": {
      if (typeof value !== "string" || !value) return NEVER
      const needle = value.toLowerCase()
      return (li) => li.sku != null && li.sku.toLowerCase().includes(needle)
    }
    case "sku_regex": {
      const re = compileSafeRegex(value)
      if (re == null) return NEVER
      return (li) => li.sku != null && li.sku.length <= MAX_SKU_LENGTH && re.test(li.sku)
    }
    case "property_value":
      return buildPropertyValueTest(value)
    case "product_id_list": {
      if (!Array.isArray(value)) return NEVER
      const ids = new Set<number>()
      for (const id of value) {
        if (typeof id === "number" && Number.isFinite(id)) ids.add(id)
      }
      if (ids.size === 0) return NEVER
      return (li) => li.product_id != null && ids.has(li.product_id)
    }
    case "sku_list": {
      if (!Array.isArray(value)) return NEVER
      const skus = new Set<string>()
      for (const s of value) {
        if (typeof s === "string" && s.length > 0) skus.add(s)
      }
      if (skus.size === 0) return NEVER
      return (li) => li.sku != null && li.sku !== "" && skus.has(li.sku)
    }
    default:
      // Unknown match_type at runtime — defensive, should never happen behind validation.
      return NEVER
  }
}

// ---------------------------------------------------------------------------
// resolveFamily — main entry point
// ---------------------------------------------------------------------------

/** A condition compiled to a reusable test plus its negate flag. */
interface PreparedFamilyCondition {
  negate: boolean
  test: ConditionTest
}

/**
 * A family rule compiled for resolution: enabled-only, sorted, with every
 * condition pre-compiled. Built once by prepareRulesForResolution and reused
 * across all line items (ingest) and all orders (recompute).
 */
export interface PreparedFamilyRule {
  id: string
  family_key: string
  combinator: FamilyMatchCombinator
  conditions: PreparedFamilyCondition[]
}

/**
 * Compile rules for resolution: drop disabled rules, sort by
 * (priority asc, created_at asc, id asc), and compile every condition's matcher
 * once (RegExp compiled, lists → Set, needles lowercased). Call this ONCE per
 * rule set and pass the result to resolveFamilyWithPreparedRules for each line
 * item — this is what keeps ingest + recompute fast.
 *
 * A rule with zero conditions compiles to a rule that never matches (defensive;
 * the DB CHECK + app validation already require ≥ 1 condition).
 */
export function prepareRulesForResolution(
  rules: ReadonlyArray<ProductFamilyRule>,
): PreparedFamilyRule[] {
  return rules
    .filter((r) => r.is_enabled)
    .slice()
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority
      if (a.created_at !== b.created_at) return a.created_at < b.created_at ? -1 : 1
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    })
    .map((rule) => ({
      id: rule.id,
      family_key: rule.family_key,
      combinator: rule.match_combinator,
      conditions: (rule.match_conditions ?? []).map((c) => ({
        negate: c.negate === true,
        test: buildConditionTest(c.match_type, c.match_value),
      })),
    }))
}

/** Evaluate one prepared condition against a line item (applies negate). */
function evaluateCondition(
  condition: PreparedFamilyCondition,
  lineItem: LineItemForResolver,
): boolean {
  const base = condition.test(lineItem)
  return condition.negate ? !base : base
}

/**
 * Does a prepared rule match a line item? Combines its conditions with the
 * rule's combinator (all = AND via every, any = OR via some) — both
 * short-circuit. A rule with no conditions never matches.
 */
function ruleMatches(rule: PreparedFamilyRule, lineItem: LineItemForResolver): boolean {
  const conds = rule.conditions
  if (conds.length === 0) return false
  if (rule.combinator === "any") {
    return conds.some((c) => evaluateCondition(c, lineItem))
  }
  return conds.every((c) => evaluateCondition(c, lineItem))
}

/**
 * Resolve a line_item to a canonical family_key. Always returns a result.
 *
 * Convenience wrapper that prepares the rules internally — use for one-shot or
 * test calls. For hot paths that resolve many line_items against the same rule
 * set (ingest, recompute), call prepareRulesForResolution() once and pass the
 * result to resolveFamilyWithPreparedRules().
 */
export function resolveFamily(
  lineItem: LineItemForResolver,
  rules: ProductFamilyRule[],
): FamilyResolution {
  return resolveFamilyWithPreparedRules(lineItem, prepareRulesForResolution(rules))
}

/**
 * Hot-path variant. Caller MUST pass rules already compiled by
 * prepareRulesForResolution() (enabled-only, sorted, matchers compiled). Use
 * when resolving many line_items against the same rule set (prepare once,
 * resolve N times).
 *
 * Priority:
 *   1. Walk prepared rules in order — first rule that matches wins.
 *   2. If no rule matches and product_id is non-null: `product_<product_id>`.
 *   3. If product_id is null (custom line items, deleted products):
 *      `line_item_<line_item_id>`.
 *
 * The fallback uses underscore (not `:` or `|`) so it's safe in any context
 * that uses those characters as composition/family delimiters.
 */
export function resolveFamilyWithPreparedRules(
  lineItem: LineItemForResolver,
  preparedRules: ReadonlyArray<PreparedFamilyRule>,
): FamilyResolution {
  for (const rule of preparedRules) {
    if (ruleMatches(rule, lineItem)) {
      return { family_key: rule.family_key, matched_rule_id: rule.id }
    }
  }
  if (lineItem.product_id != null) {
    return { family_key: `product_${lineItem.product_id}`, matched_rule_id: null }
  }
  return { family_key: `line_item_${lineItem.line_item_id}`, matched_rule_id: null }
}

